import React, {useEffect} from "react";
import {QuoationPackage} from "../../../../model/quoationPackage";
import {RadioGroup} from "../../../../components/inputs/RadioGroup";
import './MtplPackageSelectFormStyles.scss';
import {SalesPackageOption} from "../../../../components/SalesPackage/SalesPackageOption";
import {StepControls} from "../../../../components/Scenario/Step/StepControls";
import {VTPLSelectBlock} from "./VtplSelectBlock";
import {findVehicleGroup} from "../../../../model/vehicle";
import {EmptySalesPackage} from "../../../../components/SalesPackage/EmptySalesPackage";
import {OutputLink} from "../../../../components/OutputLink";
import {SimpleTooltip} from "../../../../components/SimpleTooltip";
import {CASCOPackage} from "./CascoPackage";
import {MTPLPackage} from "./MtplPackage";
import "../MTPLPersonalInfoForm/MtplPersonalInfoFormStyles.scss";
import {strings} from "../../../../strings/strings";
import {sumInsuredFormat} from "../../../../utils/format";
import {useMtlPackageSelect} from "../hooks/useMtplPackageSelect";
import {translit} from "../../../../utils/utils";
import { useHotjar } from "../../../../utils/hooks/useHotjar";
import useSwitchToNewContent from "../../../../utils/hooks/useSwitchToNewContent";

interface Props {
    packages: QuoationPackage[];
    onPackageSelect: (deductible: number, cascoCovered: boolean, totalPremium: number, vtplCovered: boolean, vtplSumInsured: number, mtplPremium: number, vtplPremium: number, cascoPremium: number) => void;
    onBackStep: () => void;
}

export const MtplPackageSelectForm = ({packages, onPackageSelect, onBackStep}: Props) => {
    const {
        quotation,
        selectedDeductible,
        selectedPackage,
        selectedProgram,
        onDeductibleChangeListener,
        onSalesProgramOptionSelect,
        setScrollToVTPLSelectBlock,
        refVTPLSelectBlock,
        MTPLIsHidden,
        CASCOIsHidden,
        VTPLIsHidden,
        vtplError,
        setVtplError,
        selectedVtplSumInsured,
        selectedVtplPremium,
        setSelectedVtplPremium,
        selectedVtplCovered
    } = useMtlPackageSelect(packages);

    const showNewContent = useSwitchToNewContent()

    const { trackHotjar} = useHotjar()

    useEffect(() => {
        trackHotjar("program", "mtpl")
    }, [])

    return (
        <div className="package-select-form">
            <div className="package-select-form__container">
                {showNewContent ? "" : (<div className="package-select-form__deductible_select">
                    <div className={'package-select-form__deductible_select__label'}>
                        {strings.franchise}
                    </div>
                    <div className={'package-select-form__deductible_select__radiogroup'}>
                        <RadioGroup
                            idPrefix={'franchise'}
                            value={selectedDeductible.toString()}
                            options={packages.map(value => ({
                                value: value.deductible.toString()
                            }))}
                            getLabel={({ value }) => `${sumInsuredFormat(value)} ${strings.currencyUAH}`}
                            onChange={
                                onDeductibleChangeListener
                            }
                        />
                    </div>

                    <div className={"package-select-form__deductible_select__tooltip"}>
                        <SimpleTooltip id={translit(strings.franchise2)} title={
                            <span><span
                                className={"accent"}>{strings.franchise2}</span>{strings.franchiseDescription}</span>}>
                            <OutputLink label={strings.whatIsFranchise} variant={"grey"} />
                        </SimpleTooltip>
                    </div>
                </div>)}
                <div className="package-select-form__salesprogram">
                    <div className='package-select-form__salesprogram__label'>
                        {strings.chooseProgram}
                    </div>

                    <div className="package-select-form__salesprogram__options">
                        {MTPLIsHidden() && (
                            <EmptySalesPackage name={strings.scenarioHeaderName}>
                                <SalesPackageOption
                                    name={strings.packageOptionEmptyText}
                                    covered={false}/>
                            </EmptySalesPackage>
                        )}

                        {!MTPLIsHidden() && (
                            <>
                                {!CASCOIsHidden() && (
                                    <CASCOPackage
                                        packageName={strings.packageCascoName}
                                        more={(
                                            <a target="_blank"
                                               rel="noopener noreferrer"
                                               href={`${process.env.PUBLIC_URL}/resources/product_conditions_direct_sales${showNewContent ? '_01.01.2025' : ''}.pdf`}>{strings.learnMoreAboutCasco}</a>
                                        )}
                                        vehicleGroup={findVehicleGroup(quotation.mtplContract.vehicle.vehicleType)}
                                        premium={selectedPackage.premium + selectedPackage.cascoAdditionalCoverage[0].premium}
                                        deductible={selectedDeductible}
                                        selected={selectedProgram?.cascoCovered}
                                        totalPremium={0}
                                        onSelect={() => {
                                            onSalesProgramOptionSelect({
                                                cascoCovered: true,
                                                mtplPremium: selectedPackage.premium,
                                                cascoPremium: selectedPackage.cascoAdditionalCoverage[0].premium,
                                                deductible: selectedPackage.deductible,
                                                totalPremium: 0,
                                            });
                                            setScrollToVTPLSelectBlock(true);
                                        }}/>
                                )}

                                <MTPLPackage
                                    packageName={strings.packageMtplName}
                                    premium={selectedPackage.premium}
                                    vehicleGroup={findVehicleGroup(quotation.mtplContract.vehicle.vehicleType)}
                                    deductible={selectedDeductible}
                                    selected={selectedProgram && !selectedProgram.cascoCovered}
                                    onSelect={() => {
                                        onSalesProgramOptionSelect({
                                            cascoCovered: false,
                                            mtplPremium: selectedPackage.premium,
                                            cascoPremium: 0,
                                            deductible: selectedPackage.deductible,
                                            totalPremium: selectedPackage.premium,
                                        });
                                        setScrollToVTPLSelectBlock(true);
                                    }}/>
                            </>
                        )}
                    </div>
                </div>

                <div className="package-select-form__vtpl"
                     ref={refVTPLSelectBlock}
                     hidden={VTPLIsHidden()}>
                    <div className="package-select-form__vtpl__options">
                        <VTPLSelectBlock
                            isSmartDrive={false}
                            error={vtplError}
                            onSelectListener={(sumInsured: number) => {
                                setVtplError(false);
                            }}
                            vtplOptions={selectedPackage.vtplAdditionalCoverage.map(value => {
                                return {
                                    premium: value.premium,
                                    sumInsured: value.sumInsured,
                                    totalPremium: 0
                                };
                            }).filter((_, index) => index !== 2 && index !== 3)}/>
                    </div>
                </div>
            </div>

            <StepControls nextIsAvailable={selectedProgram !== undefined}
                          onNext={() => {
                              if (selectedVtplSumInsured === undefined && !VTPLIsHidden()) {
                                  setVtplError(true);
                              } else {
                                  let vtplCoveragePremium = selectedVtplPremium;

                                  if (selectedVtplSumInsured) {
                                      const vtplCoverage = selectedPackage.vtplAdditionalCoverage.find(value =>
                                          value.sumInsured === selectedVtplSumInsured);

                                      setSelectedVtplPremium(vtplCoverage.premium);
                                      vtplCoveragePremium = vtplCoverage.premium;
                                  }

                                  onPackageSelect(
                                      selectedProgram.deductible,
                                      selectedProgram.cascoCovered,
                                      selectedProgram.totalPremium,
                                      selectedVtplCovered,
                                      selectedVtplSumInsured,
                                      selectedProgram.mtplPremium,
                                      vtplCoveragePremium,
                                      selectedProgram.cascoPremium
                                  )
                              }
                          }}
                          onBack={() => {
                            onBackStep();
                          }}
                          leftSteps={2}/>
        </div>
    );
};